import React, { useEffect, useState } from "react";
import MerchantAccountStore from "../MerchantAccountStore";
import { observer } from "mobx-react";
import MindMePaymentsLogo from "../../../../../../images/logos/mindme-payment-logo.svg";
import { NavigateFunction } from "react-router-dom";
import { firstValueFrom } from "rxjs";
import {
  DescriptionCell,
  ItemDetailMainWrapper,
  LabelCell,
  MainOuterCard,
  ProductDetailWrapper,
} from "../../../shared/styles";
import {
  AuthService,
  CollapsiblePanel,
  CustomLabel,
  FlexContainer,
  IOption,
  MadisonBlueButton,
  RedButton,
  SapphireBlueButton,
  SingleSelect,
  ToastService,
  UtilityMethods,
  withRouter,
} from "@mindmemobile/mm-shared-global";
import { ApplicationStatus } from "../../../shared/enums";
import {
  MerchantAccount,
  MerchantPaymentModel,
  SelectedItemModel,
} from "../../../shared/models";
import { MerchantAccountService } from "../../../shared/services";
import {
  AccountSettingsStore,
  ItemsStore,
  RolesAndPermissionStore,
} from "../../../shared/store";
import DeleteMerchantAccountPopUp from "./DeleteMerchantAccountPopUp";
import { DashboardTopBar, ItemContentArea } from "../../../shared/components";

type IProps = {
  merchantAccountStore: MerchantAccountStore;
  id: string | null;
  navigate: NavigateFunction;
  rolesAndPermissionStore: RolesAndPermissionStore;
  itemStore: ItemsStore;
};
const MerchantAccountDetail: React.FC<IProps> = ({
  merchantAccountStore,
  id,
  navigate,
  rolesAndPermissionStore,
  itemStore,
}) => {
  const [selectedKey, setSelectedKey] = useState<
    string | keyof typeof ApplicationStatus
  >();
  const [showDeletePopup, setShowDeletePopup] = useState(false);

  const merchantAccount = merchantAccountStore.merchantAccounts.find(
    (item) => item.id === id,
  );

  if (!merchantAccount) {
    setTimeout(() => redirectToMerchants(), 100);
    return null;
  }

  useEffect(() => {
    setSelectedKey(merchantAccount.applicationStatus);
    itemStore?.setBreadCrumbData(
      new SelectedItemModel({
        route: "/app/merchant-accounts",
        title: merchantAccount.name || "nfn",
      }),
    );

    return () => {
      itemStore?.setBreadCrumbData();
    };
  }, [merchantAccount]);

  if (!id) {
    return null;
  }

  function topBar(
    node: React.ReactNode,
    rightContent: React.ReactNode,
    showNoMerchantAccountNotification?: boolean,
  ) {
    return (
      <>
        <DashboardTopBar
          showNoMerchantAccountNotification={showNoMerchantAccountNotification}
          itemStore={itemStore}
          rightContent={rightContent}
          showRightContent={showNoMerchantAccountNotification !== true}
          rolesAndPermissionStore={rolesAndPermissionStore}
        />
        <ItemContentArea itemStore={itemStore} node={node} />
      </>
    );
  }

  const handleNavigation = (direction: "back" | "next") => {
    const newItem =
      direction === "next" ? getNextAccountId() : getPreviousAccountId();
    if (newItem) {
      navigate(`/app/merchant-accounts/details/${newItem.id}`);
    } else {
      ToastService.showError("No more items in this direction.");
    }
  };

  const getNextAccountId = () => {
    const currentIndex = merchantAccountStore.merchantAccounts.findIndex(
      (item) => item.id === merchantAccount.id,
    );
    if (
      currentIndex !== -1 &&
      currentIndex < merchantAccountStore.merchantAccounts.length - 1
    ) {
      return merchantAccountStore.merchantAccounts[currentIndex + 1];
    }
    return null;
  };

  const getPreviousAccountId = () => {
    const currentIndex = merchantAccountStore.merchantAccounts.findIndex(
      (item) => item.id === merchantAccount.id,
    );
    if (currentIndex > 0) {
      return merchantAccountStore.merchantAccounts[currentIndex - 1];
    }
    return null;
  };

  const getCurrentAccountPosition = () => {
    const currentIndex = merchantAccountStore.merchantAccounts.findIndex(
      (item) => item.id === merchantAccount.id,
    );
    return currentIndex !== -1 ? currentIndex + 1 : null;
  };

  const nextAndBackButtons = () => {
    const currentPosition = getCurrentAccountPosition();
    return (
      <div>
        {" "}
        <FlexContainer
          alignItems="center"
          spacing={10}
          justifyContent="flex-end"
          margin="0px 20px 0px"
          width="auto"
        >
          <MadisonBlueButton
            minWidth={""}
            onClick={() => handleNavigation("back")}
            disabled={!getPreviousAccountId()}
            lineHeight="inherit"
            padding="0px 15px"
          >
            Back
          </MadisonBlueButton>
          <CustomLabel fontSize={"20px"}>
            {currentPosition
              ? `${currentPosition} of ${merchantAccountStore.merchantAccounts.length}`
              : `0 of ${merchantAccountStore.merchantAccounts.length}`}
          </CustomLabel>
          <MadisonBlueButton
            minWidth={""}
            onClick={() => handleNavigation("next")}
            disabled={!getNextAccountId()}
            lineHeight="inherit"
            padding="0px 15px"
          >
            Next
          </MadisonBlueButton>
        </FlexContainer>
      </div>
    );
  };

  const getMenuItems = (merchantAccount: MerchantAccount): IOption[] => {
    switch (
      merchantAccount.applicationStatus as keyof typeof ApplicationStatus
    ) {
      case ApplicationStatus.Pending:
        return [
          // "Complete Application" is the Fiserv hosted-application flow and is
          // meaningless for NMI (no application link). Only offer it for Fiserv,
          // matching the card's getMenuItems gating.
          ...(merchantAccount.isFiservMerchantAccount()
            ? [
                {
                  label: "Complete Application",
                  value: ApplicationStatus.Complete,
                },
              ]
            : []),
          { label: "Cancel Application", value: ApplicationStatus.Cancelled },
          { label: "Pending", value: ApplicationStatus.Pending },
        ];
      case ApplicationStatus.Active:
        return [
          ...(merchantAccount.isDefault
            ? []
            : [
                {
                  label: "Set as default",
                  value: ApplicationStatus.SetDefault,
                },
              ]),
          { label: "Make Inactive", value: ApplicationStatus.Inactive },
          { label: "Active", value: ApplicationStatus.Active },
        ];
      case ApplicationStatus.Inactive:
        return [
          { label: "Make Active", value: ApplicationStatus.Active },
          { label: "Inactive", value: ApplicationStatus.Inactive },
        ];
      case "InReview" as keyof typeof ApplicationStatus:
        return [
          {
            label: "In Review",
            value: "InReview" as keyof typeof ApplicationStatus,
          },
        ];
      case ApplicationStatus.Cancelled:
        return [{ label: "Cancelled", value: ApplicationStatus.Cancelled }];
      default:
        return [];
    }
  };

  const onClickDeleteAccount = () => {
    if (!rolesAndPermissionStore.hasManagePermission("Delete")) return;
    setShowDeletePopup(true);
  };

  const handleDropdownChange = (option: IOption) =>
    setSelectedKey(option.value);

  const handleCompleteApplication = (item: MerchantAccount) => {
    if (
      !rolesAndPermissionStore.hasManageMerchantAccountPermission(
        "Complete the Application",
      )
    ) {
      return;
    }
    merchantAccountStore.fetchCompleteApplicationLink(item.applicationId);
  };

  const handleSetDefaultApplication = (item: MerchantAccount) => {
    if (!rolesAndPermissionStore.hasManagePermission("edit the application"))
      return;
    console.log("set default");
    merchantAccountStore.setDefaultMerchantAccount(item.id);
  };

  const handleApplicationUpdate = async (
    item: MerchantAccount,
    action: keyof typeof ApplicationStatus | "Delete",
  ) => {
    const data = item.createAPIModel2();
    const apiModel = {
      accountId: AuthService.authData.accountId,
      merchantAccount: data,
    };

    let result;
    if (action === "Delete") {
      return;
    } else if (action === ApplicationStatus.Cancelled) {
      result = await firstValueFrom(
        MerchantAccountService.cancelMerchantAccount(apiModel),
      );
    } else if (action !== ApplicationStatus.Pending) {
      result = await merchantAccountStore.updateApplicationState(
        item.id,
        action,
      );
    }

    if (action !== ApplicationStatus.Pending) {
      if (!result?.result) {
        ToastService.showError(`Error in updating merchant account`);
      } else {
        merchantAccountStore.fetchMerchantAccounts();
        ToastService.showSuccess(`Merchant Account updated successfully`);
      }
    }
  };

  const onClickSave = () => {
    if (!rolesAndPermissionStore.hasManagePermission("Edit")) return;
    const menuItems = getMenuItems(merchantAccount);
    const selectedMenuItem = menuItems.find(
      (item) => item.value === selectedKey,
    );
    console.log(selectedMenuItem, selectedKey);
    if (selectedMenuItem) {
      switch (selectedKey as keyof typeof ApplicationStatus) {
        case "Complete":
          handleCompleteApplication(merchantAccount);
          break;
        case "SetDefault":
          handleSetDefaultApplication(merchantAccount);
          break;
        default:
          handleApplicationUpdate(
            merchantAccount,
            selectedKey as keyof typeof ApplicationStatus,
          );
          break;
      }
    }
  };

  const row = (label: string, content: React.ReactNode, className?: string) => {
    return (
      <FlexContainer
        spacing={120}
        key={label}
        className={`label-description-row ${className}`}
      >
        <LabelCell className="merchant-label-cell">
          <CustomLabel lineHeight="40px" fontSize="16px">
            {label}
          </CustomLabel>
        </LabelCell>
        <DescriptionCell className="merchant-description-cell">
          {content}
        </DescriptionCell>
      </FlexContainer>
    );
  };

  function redirectToMerchants() {
    navigate("/app/merchant-accounts/");
  }

  function paymentDescription(payment: MerchantPaymentModel) {
    let descripton: any = "-";
    if (payment.isRefund) {
      descripton =
        payment.amount > 0 && payment?.date
          ? `$${AccountSettingsStore.formatAmount(payment?.amount)} refunded on ${UtilityMethods.formatDateInString(payment?.date)}`
          : "-";
    } else {
      descripton =
        payment.amount > 0 && payment?.date
          ? `$${AccountSettingsStore.formatAmount(payment?.amount)} paid on ${UtilityMethods.formatDateInString(payment?.date)}`
          : "-";
    }

    return <>{descripton}</>;
  }

  return topBar(
    <ProductDetailWrapper>
      <ItemDetailMainWrapper className="merchant-account-wrapper">
        <MainOuterCard>
          {showDeletePopup && (
            <DeleteMerchantAccountPopUp
              merchantAccount={merchantAccount}
              onClose={(isDeleted) => {
                setShowDeletePopup(false);
                if (isDeleted) {
                  redirectToMerchants();
                }
              }}
            />
          )}
          <FlexContainer fxLayout="column" spacing={25}>
            <CustomLabel
              fontWeight={"600"}
              fontSize={"30px"}
              lineHeight="normal"
              margin="0px 0px 5px"
            >
              Merchant Account
            </CustomLabel>
            <CollapsiblePanel
              title="Details"
              collapsibleClassName="detail-sub-panel"
              className="merchant-collapsible-panel"
            >
              {row(
                "Status",
                <SingleSelect
                  hasError={false}
                  selectedKey={selectedKey || ""}
                  nameKeys={["label"]}
                  valueKeys={["value"]}
                  options={[getMenuItems(merchantAccount)]}
                  onChange={handleDropdownChange}
                />,
                "merchant-status-row",
              )}
              {row(
                "Merchant Name",
                <CustomLabel> {merchantAccount.name}</CustomLabel>,
              )}
              {row(
                "Processor",
                <img src={merchantAccount.getLogo()} />,
                "processor-row",
              )}
              {row(
                "Gateway",
                <CustomLabel>{merchantAccount.gateway}</CustomLabel>,
              )}
              {row(
                "Statement Descriptor",
                <CustomLabel>
                  {" "}
                  {merchantAccount.statementDescriptor || ""}
                </CustomLabel>,
              )}
              {row(
                "Primary Currency",
                <CustomLabel>$ - US Dollar</CustomLabel>,
              )}
            </CollapsiblePanel>
            <CollapsiblePanel
              title="Activity"
              collapsibleClassName="detail-sub-panel"
            >
              <FlexContainer fxLayout="column">
                {row(
                  "Created",
                  <CustomLabel>
                    {UtilityMethods.formatDateInString(
                      UtilityMethods.removeZFromDateString(
                        merchantAccount.createdDate,
                      ),
                    )}
                  </CustomLabel>,
                )}
                {row(
                  "Last Payment",
                  <CustomLabel>
                    {paymentDescription(merchantAccount.lastPayment)}
                  </CustomLabel>,
                )}
                {row(
                  "Total Processed",
                  <CustomLabel>
                    {`$${AccountSettingsStore.formatAmount(merchantAccount.totalProcessed)}`}
                  </CustomLabel>,
                )}
                {row(
                  "First Payment",
                  <CustomLabel>
                    {paymentDescription(merchantAccount.firstPayment)}
                  </CustomLabel>,
                )}
              </FlexContainer>
            </CollapsiblePanel>
            <FlexContainer
              alignItems="center"
              justifyContent="center"
              spacing={20}
            >
              {merchantAccount.applicationStatus !==
                ApplicationStatus.Pending && (
                <RedButton
                  height="40px"
                  primary
                  onClick={() => onClickDeleteAccount()}
                >
                  Delete Account
                </RedButton>
              )}
              <RedButton
                height="40px"
                onClick={() =>
                  setSelectedKey(merchantAccount.applicationStatus)
                }
                disabled={merchantAccount.applicationStatus === selectedKey}
              >
                Do Not Save
              </RedButton>
              <SapphireBlueButton
                height="40px"
                primary
                onClick={onClickSave}
                disabled={merchantAccount.applicationStatus === selectedKey}
              >
                Save
              </SapphireBlueButton>
            </FlexContainer>
          </FlexContainer>
        </MainOuterCard>
      </ItemDetailMainWrapper>
    </ProductDetailWrapper>,
    nextAndBackButtons(),
    merchantAccountStore.merchantAccounts.length === 0,
  );
};

export default withRouter(observer(MerchantAccountDetail));
